import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchMentorDashboardData,
  fetchPoapDescriptions,
  updateApprovalStatus,
  completeStep,
  fetchMentorClassrooms,
  fetchApprovalRequests,
} from 'api';
import { fireSwal } from 'utils/swalConfig';

const normalizeToQueueItem = (a) => {
  if (!a) return null;
  return {
    id: a.id || a.approvalRequestId,
    createdAt: a.createdAt,
    team: {
      id: a.team?.id || a.teamId,
      name: a.team?.name || a.teamName || 'Unknown Team',
    },
    step: {
      step: a.step?.step || a.stepName || 'Unnamed Step',
      poapTitle: a.step?.poapTitle || a.teamStep?.poapStep?.category?.poapDescription?.title || 'Untitled POAP',
      // Prioritize a dedicated description field, fall back to the step name if needed.
      description: a.shortDescription || a.step?.description || 'No description available for this activity.',
      points: a.step?.points ?? a.stepPoints ?? 0,
      poapCategoryId: a.step?.poapCategoryId || a.teamStep?.poapStep?.poapCategoryId,
    },
    _raw: a,
  };
};

export const useTeamManagement = () => {
  const queryClient = useQueryClient();

  // --- QUERIES ---
  const { data: dashboardData, isLoading: isLoadingDashboard } = useQuery({
    queryKey: ['mentorDashboardData'],
    queryFn: fetchMentorDashboardData,
  });

  const { data: approvalsData, isLoading: isLoadingApprovals } = useQuery({
    queryKey: ['approvalRequests'],
    queryFn: fetchApprovalRequests,
    refetchOnWindowFocus: true,
  });

  const { data: classrooms, isLoading: isLoadingClassrooms } = useQuery({
    queryKey: ['classrooms'],
    queryFn: fetchMentorClassrooms,
  });

  const { data: poaps, isLoading: isLoadingPoaps } = useQuery({
    queryKey: ['poapDescriptions'],
    queryFn: fetchPoapDescriptions,
  });

  // --- MEMOIZED SELECTORS ---
  const pendingApprovals = useMemo(() =>
    (approvalsData ?? [])
      .filter(a => a?.status === 'pending')
      .map(normalizeToQueueItem)
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
    [approvalsData]
  );

  const teamsWithStats = useMemo(() => {
    if (!dashboardData?.teamsWithStats || !classrooms) return [];
    const classroomMap = new Map(classrooms.map(c => [c.id, c.name]));
    return dashboardData.teamsWithStats.map(team => ({
      ...team,
      classroom: classroomMap.get(team.classroomId) || null,
    }));
  }, [dashboardData, classrooms]);

  // --- MUTATIONS ---
  const updateApprovalStatusMutation = useMutation({
    mutationFn: ({ approvalId, status }) => updateApprovalStatus(approvalId, status),
    onSuccess: (_, variables) => {
      const title = variables.status === 'approved' ? 'Step Approved!' : 'Request Denied';
      fireSwal({ icon: 'success', title, timer: 1500, showConfirmButton: false });
      queryClient.invalidateQueries({ queryKey: ['approvalRequests'] });
      queryClient.invalidateQueries({ queryKey: ['mentorDashboardData'] });
    },
    onError: (error) => fireSwal({ icon: 'error', title: 'Update Failed', text: error.message }),
  });

  const completeStepMutation = useMutation({
    mutationFn: ({ teamId, stepId, descriptionId }) => completeStep(teamId, stepId, descriptionId),
    onError: (error) => fireSwal({ icon: 'error', title: 'Action Failed', text: `Could not award points for the step. ${error.message}` }),
  });

  // --- ACTION HANDLERS ---
  const approveRequest = async (normalizedRequest) => {
    const request = normalizedRequest._raw;
    if (!request || !request.step || !request.team) {
      fireSwal({ icon: 'error', title: 'Error', text: 'Invalid request data provided.' });
      return;
    }

    const poap = (poaps || []).find(p =>
      p.categories.some(c => c.id === request.step.poapCategoryId)
    );

    if (!poap) {
      fireSwal({ icon: 'error', title: 'Error', text: 'Could not find the associated POAP for this step.' });
      return;
    }

    await completeStepMutation.mutateAsync({
      teamId: request.teamId,
      stepId: request.step.id,
      descriptionId: poap.id
    });

    await updateApprovalStatusMutation.mutateAsync({
      approvalId: request.id,
      status: 'approved'
    });
  };

  const denyRequest = (approvalId) => {
    updateApprovalStatusMutation.mutate({ approvalId, status: 'denied' });
  };

  return {
    isLoading: isLoadingDashboard || isLoadingPoaps || isLoadingClassrooms || isLoadingApprovals,
    teamsWithStats,
    poaps: poaps || [],
    pendingApprovals,
    actions: {
      approveRequest,
      denyRequest,
      refetchApprovals: () => queryClient.invalidateQueries({ queryKey: ['approvalRequests'] }),
    },
  };
};